import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { useMutation } from '@apollo/react-hooks'
import { useDispatch } from 'react-redux'
import qs from 'query-string'

import Input from '../../../../components/form-fields/input'
import PasswordInput from '../../../../components/form-fields/password-input'
import Checkbox from '../../../../components/form-fields/checkbox'
import Button from '../../../../components/button'
import FullScreenLoadingComponent from '../../../../components/loading/fullscreen-loader'

import {
  STATIC_SCREENS,
  SIGN_UP_SCREENS,
  FORM_VALUES,
  FORM_MESSAGES,
  MODALS
} from '../../../../constants'
import { STORAGE_KEY_EMAIL_ON_GUEST_HOME_PAGE } from '../../../../storage-keys'
import { REGEX } from '../../../../utils/regex'
import { useFocusOnMount } from '../../hooks/use-focus-on-mount'
import { handleGATrack } from '../../utils/handle-ga-track'
import { getCookieValue } from '../../utils/get-cookie-value'
import { getGQLErrorCode, getGQLErrorMsg } from '../../../../lib/apollo'
import { segmentIdentify } from '../../../../segment/segment-identify'
import { segmentTrackSignup } from '../../../../segment/segment-track'
import { login } from '../../../../actions'
import ACCOUNT_MUTATION from '../../../../../graphql/mutations/account.gql'
import styles from '../sign-up.css'

/**
 * CreateAccountForm component for creating a new user account
 */
const CreateAccountForm = () => {
  const currentScreen = SIGN_UP_SCREENS.CREATE_ACCOUNT
  const firstElementRef = useFocusOnMount()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()

  const [createAccount, { loading: isCreatingAccount }] = useMutation(ACCOUNT_MUTATION)

  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitted },
    setError,
    setValue,
    getValues
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      acceptTerms: false,
      optInToNewsletter: false
    }
  })

  // Initialize email from guest home page or cookies
  useEffect(() => {
    // Initialize email which get from the guest home page emailBox if it exists
    const existingEmail = sessionStorage.getItem(STORAGE_KEY_EMAIL_ON_GUEST_HOME_PAGE)
    if (existingEmail) {
      setValue('email', existingEmail)
    }

    // Initialize email which get from neon welcome page emailBox if it exists
    const emailInCookies = getCookieValue('email')
    if (emailInCookies) {
      setValue('email', emailInCookies)
    }
  }, [setValue])

  // Report validation errors when form submission fails validation
  const reportValidationErrors = (validationErrors) => {
    Object.keys(validationErrors).forEach((fieldName) => {
      const error = validationErrors[fieldName]
      const values = getValues()

      handleGATrack(
        { name: fieldName, value: values[fieldName] },
        { message: error.message },
        currentScreen.datalayerStepSequence,
        currentScreen.datalayerStepLabel,
        true
      )
    })
  }

  const onSubmit = async (formData) => {
    try {
      const { data } = await createAccount({
        variables: {
          input: {
            email: formData.email,
            password: formData.password,
            optIns: [
              {
                id: 'RECEIVE_UPDATES',
                subscribed: formData.optInToNewsletter
              }
            ]
          }
        }
      })

      // Destructure account from response
      const account = data?.account

      // Handle GraphQL errors
      const firstError = account?.errors?.[0]?.message
      if (firstError) {
        setError('root.formError', { message: firstError })
        return
      }

      // Add segment data identify for sign up
      const currentProfile = account?.profiles?.find(
        item => item.id === account?.selectedProfile
      )
      segmentIdentify(account, currentProfile)

      // Add segment data analytics for sign up
      segmentTrackSignup({
        email: formData.email
      })

      // Clear signup form value and log in user
      dispatch(login(account, true))

      // Navigate to plan intro page on success
      history.push(SIGN_UP_SCREENS.PLAN_INTRO.path)
    } catch (error) {
      // Handle network or GraphQL errors
      const errorCode = error.graphQLErrors && getGQLErrorCode(error)
      const errorMessage = error.graphQLErrors
        ? getGQLErrorMsg(error)
        : error.message || 'An error occurred. Please try again.'

      // won't be triggered due to MW change (LBX-9576)
      if (errorCode && ['USER_EXISTS', 'INVALID_CREDENTIALS'].includes(errorCode)) {
        history.replace({
          ...location,
          search: qs.stringify({
            ...qs.parse(location.search),
            modal: MODALS.qsParams.userExists,
            email: formData.email
          })
        })
      }

      setError('root.formError', {
        message: errorMessage
      })

      // Track API error
      handleGATrack(
        { name: 'formError', value: formData.email },
        { message: errorMessage },
        currentScreen.datalayerStepSequence,
        currentScreen.datalayerStepLabel,
        isSubmitted
      )
    }
  }

  return (
    <>
      <form
        className={styles.form}
        onSubmit={handleFormSubmit(onSubmit, reportValidationErrors)}
        autoComplete="off"
      >
        <Controller
          name="email"
          control={control}
          rules={{
            required: FORM_MESSAGES.required,
            pattern: {
              value: REGEX.email,
              message: FORM_MESSAGES.email.valid
            }
          }}
          render={({ field }) => (
            <Input
              label="Email"
              {...field}
              error={errors[field.name]?.message}
              onBlur={() => {
                field.onBlur()
                handleGATrack(field, errors[field.name], currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel, isSubmitted)
              }}
              disabled={isCreatingAccount}
              data-e2e="email-signup"
              autoComplete="new-off"
              ref={firstElementRef}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          rules={{
            required: FORM_MESSAGES.required,
            minLength: {
              value: FORM_VALUES.password.min,
              message: FORM_MESSAGES.password.min
            }
          }}
          render={({ field }) => (
            <PasswordInput
              label="Choose a password"
              {...field}
              error={errors[field.name]?.message}
              onBlur={() => {
                field.onBlur()
                handleGATrack(field, errors[field.name], currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel, isSubmitted)
              }}
              disabled={isCreatingAccount}
              data-e2e="password-signup"
              autoComplete="new-off"
            />
          )}
        />

        <Controller
          name="acceptTerms"
          control={control}
          rules={{
            required: 'You must agree to the terms of use and privacy policy'
          }}
          render={({ field: { value, onChange, ...field } }) => (
            <Checkbox
              label={(
                <span>
                  I agree to the Neon
                  {' '}
                  <a href={STATIC_SCREENS.TERMS} target="_blank" rel="noopener noreferrer">
                    Terms of Use
                  </a>
                  {' '}
                  and
                  {' '}
                  <a href={STATIC_SCREENS.PRIVACY} target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </span>
              )}
              {...field}
              checked={value}
              onChange={(e) => {
                const newValue = e.target.checked
                onChange(newValue)
                handleGATrack({ ...field, value: newValue }, errors[field.name], currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel, isSubmitted)
              }}
              error={errors[field.name]?.message}
              disabled={isCreatingAccount}
              data-e2e="terms-signup-checkbox"
            />
          )}
        />

        <Controller
          name="optInToNewsletter"
          control={control}
          render={({ field: { value, onChange, ...field } }) => (
            <Checkbox
              label="I am happy to receive emails promoting Neon services"
              {...field}
              checked={value}
              onChange={(e) => {
                const newValue = e.target.checked
                onChange(newValue)
                handleGATrack({ ...field, value: newValue }, errors[field.name], currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel, isSubmitted)
              }}
              error={errors[field.name]?.message}
              disabled={isCreatingAccount}
              data-e2e="newsletter-signup-checkbox"
            />
          )}
        />

        <>
          <Button
            className={styles.submit}
            text="Create account"
            type="submit"
            disabled={isCreatingAccount}
            e2eIdentifier="create-account-button-signup-submit"
          />

          {errors.root?.formError && (
            <p className={styles.errorMessage}>
              {errors.root.formError.message}
            </p>
          )}
        </>
      </form>

      <FullScreenLoadingComponent visible={isCreatingAccount} data-e2e="signup-loader-visible" />
    </>
  )
}

export default CreateAccountForm
